import { tx as translateInterfaceText } from '@/utils/i18n';
import { clearAuthStorage } from '@/utils/authStorage';
import axios, { InternalAxiosRequestConfig } from 'axios';
import { unknown } from 'zod';

// Get API URL from environment variables
const API_URL = import.meta.env.VITE_API || 'https://api.hostvsl.com.br';


// Create axios instance with base configuration
// Default timeout is 2 minutes (suficiente para requisições normais).
// Uploads de vídeo precisam setar timeout maior na própria chamada (ex.: hierarchicalFoldersService.uploadVideos).
export const authApi = axios.create({
  baseURL: API_URL,
  timeout: 2 * 60 * 1000, // 2 minutes
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // Importante para cookies (refresh token)
});

let getIp = "";
(async () => {
  const ipRes = await fetch('https://api.ipify.org?format=json');
  const ipData = await ipRes.json();
  getIp = ipData.ip;
  authApi.defaults.headers.common['ip'] = `${getIp}`;
  authApi.defaults.headers.common['Content-Type'] = `application/json`;
})()

// Flag para evitar loop infinito no refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add request interceptor to include auth token
authApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
authApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Se erro 401 e não é rota de login/refresh
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/login') &&
      !originalRequest.url?.includes('/refresh')
    ) {
      if (isRefreshing) {
        // Se já está fazendo refresh, coloca requisição na fila
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return authApi(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Tenta renovar o token
        const response = await authApi.post('/auth/refresh');
        const newToken = response.data.token;

        // Salva novo token
        const loginChecked = localStorage.getItem('checkedLogin');
        if (loginChecked) {
          localStorage.setItem('token', newToken);
        } else {
          sessionStorage.setItem('token', newToken);
        }

        // Atualiza header e processa fila
        authApi.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        processQueue(null, newToken);

        // Refaz requisição original
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return authApi(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Token expirado, faz logout
        clearAuthStorage();
        window.location.href = '/login';

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Response types
interface LoginResponse {
  success: boolean;
  message?: string;
  user?: any;
  user_id?: string;
  token?: string;
  plano?: any;
}

interface ApiResponse<T = any> {
  message: (a: any) => unknown | string;
  data: T;
  status: number;
  statusText: string;
}

// Auth service
export const authService = {
  login: async (credentials: { email: string; senha: string }): Promise<ApiResponse<LoginResponse>> => {
    try {
      const response = await authApi.post<LoginResponse>('/login', credentials, {
        headers: {
          ip: getIp // substitua getIp pelo valor real que você deseja definir
        }
      });
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao fazer login") } };
    }
  },

  /**
   * GET /me - Busca dados atualizados do usuário autenticado
   * Token JWT é enviado automaticamente via interceptor
   */
  getMe: async (): Promise<ApiResponse<any>> => {
    try {
      const response = await authApi.get('/auth/me', {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao buscar dados do usuário") } };
    }
  },

  /**
   * POST /refresh - Renova access token
   * Refresh token enviado via cookie automaticamente
   */
  refreshToken: async (): Promise<ApiResponse<{ token: string }>> => {
    try {
      const response = await authApi.post('/auth/refresh');
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao renovar token") } };
    }
  },

  /**
   * POST /logout - Faz logout e limpa refresh token cookie
   */
  logout: async (): Promise<ApiResponse<any>> => {
    try {
      const response = await authApi.post('/auth/logout');
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao fazer logout") } };
    }
  },

  supportLogin: async (
    credentials: { email: string; senha: string },
    supportToken: string
  ): Promise<ApiResponse<LoginResponse>> => {
    try {
      const response = await authApi.post<LoginResponse>('/support-request/login', credentials, {
        headers: {
          'x-support-token': supportToken,
        },
      });
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao fazer login de suporte") } };
    }
  },

  createSupportToken: async (userId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await authApi.post('/support-request', { userId });
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao criar token de suporte") } };
    }
  },

  sendEmailRecovery: async (email: string): Promise<ApiResponse<any>> => {
    try {
      const response = await authApi.patch('/usuario/send-code', { email });
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao enviar e-mail de recuperação") } };
    }
  },

  validateCode: async (email: string, code: string): Promise<ApiResponse<any>> => {
    try {
      const response = await authApi.patch('/usuario/validation-code', { email, code });
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao validar código") } };
    }
  },

  newPassword: async (email: string, newPassword: string, code: string): Promise<ApiResponse<any>> => {
    try {
      const response = await authApi.patch('/usuario/change-password', { email, newPassword, code });
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao alterar senha") } };
    }
  },

  cadastro: async (userData: {
    nome: string;
    sobrenome: string;
    email: string;
    cpf: string;
    senha: string;
    telefone?: string;
    seller?: string;
    indicator?: string;
  }): Promise<ApiResponse<any>> => {
    try {
      const response = await authApi.post('/usuario/cadastro', userData, {
        headers: {
          ip: getIp // substitua getIp pelo valor real que você deseja definir
        }
      });
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao realizar cadastro") } };
    }
  },

  /**
   * POST /change-password - Altera senha do usuário autenticado
   * Token JWT é enviado automaticamente via interceptor
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<ApiResponse<any>> => {
    try {
      const response = await authApi.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      return response as any;
    } catch (error: any) {
      return error?.response?.data || { data: { success: false, message: translateInterfaceText("Erro ao alterar senha") } };
    }
  },
};
